import React from "react";
import styled from "styled-components";
import { css } from "styled-components/macro"; // eslint-disable-line
import { motion } from "framer-motion";
import { ShieldCheck, Mail, Phone } from "lucide-react";
import LandingLayout from "layouts/LandingLayout";
import Hero, { SlantedBackground } from "components/hero/BackgroundAsImage";
import useInView from "helpers/useInView";
import { fadeUpVariants } from "lib/animations";
import config from "lib/config";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C_PRIMARY = "#ff6c00";
const C_SECONDARY = "#111111";

// ─── Animation ───────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const FadeUpSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ once: true });
  return (
    <MDiv
      ref={ref as React.Ref<HTMLDivElement>}
      variants={fadeUpVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
    >
      {children}
    </MDiv>
  );
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 5rem 1.5rem 6rem;
`;

const DocCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 3rem;

  @media (max-width: 640px) {
    padding: 1.75rem;
  }
`;

const DocHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const DocHeaderIcon = styled.div`
  width: 3rem;
  height: 3rem;
  background: #fff4ec;
  border-left: 3px solid ${C_PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${C_PRIMARY};
  flex-shrink: 0;
`;

const DocMeta = styled.div``;

const DocTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.2rem;
`;

const DocDate = styled.p`
  font-size: 0.72rem;
  color: #9ca3af;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin: 2rem 0 0.75rem;
  padding-left: 0.75rem;
  border-left: 3px solid ${C_PRIMARY};
`;

const Paragraph = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.85;
  margin-bottom: 0.85rem;
`;

const BulletList = styled.ul`
  margin: 0.5rem 0 1rem 1.25rem;
  list-style: disc;
`;

const BulletItem = styled.li`
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.85;
  margin-bottom: 0.3rem;
`;

const ContactBox = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: #fafafa;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactBoxTitle = styled.p`
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #9ca3af;
  margin-bottom: 0.25rem;
`;

const ContactLine = styled.p`
  font-size: 0.875rem;
  color: ${C_SECONDARY};
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const PrivacyPage: React.FC = () => (
  <LandingLayout disableAnimation>
    <Hero
      backgroundCss={`linear-gradient(135deg, ${C_SECONDARY} 0%, #1a1a1a 100%)`}
      notification="Confidentialité"
      heading={
        <>
          <span>Politique de</span>
          <br />
          <SlantedBackground>confidentialité.</SlantedBackground>
        </>
      }
      description="Comment nous collectons, utilisons et protégeons vos données personnelles."
    />

    <PageWrapper>
      <FadeUpSection>
        <DocCard>
          <DocHeader>
            <DocHeaderIcon>
              <ShieldCheck size={22} />
            </DocHeaderIcon>
            <DocMeta>
              <DocTitle>Politique de confidentialité</DocTitle>
              <DocDate>Dernière mise à jour : 1er janvier 2026</DocDate>
            </DocMeta>
          </DocHeader>

          {/* ── Introduction ── */}
          <Paragraph>
            {config.appName} {config.subAppName} (ci-après « nous ») s'engage à
            protéger la vie privée des utilisateurs de son site et de ses
            services. La présente politique décrit quelles données nous
            collectons, pourquoi, comment nous les utilisons et vos droits à
            leur égard.
          </Paragraph>

          {/* ── 1 ── */}
          <SectionTitle>1. Responsable du traitement</SectionTitle>
          <Paragraph>
            Le responsable du traitement est {config.appName}{" "}
            {config.subAppName}, société immatriculée au registre du commerce.
            Pour toute question relative à vos données, vous pouvez nous
            contacter à l'adresse indiquée en bas de cette page.
          </Paragraph>

          {/* ── 2 ── */}
          <SectionTitle>2. Données collectées</SectionTitle>
          <Paragraph>
            Dans le cadre de nos services, nous collectons les données suivantes
            :
          </Paragraph>
          <BulletList>
            <BulletItem>
              <strong>Données d'identification</strong> : nom, prénom, date de
              naissance, nationalité.
            </BulletItem>
            <BulletItem>
              <strong>Données de contact</strong> : adresse e-mail, numéro de
              téléphone / WhatsApp.
            </BulletItem>
            <BulletItem>
              <strong>Données académiques</strong> : établissement de
              destination, filière, année académique.
            </BulletItem>
            <BulletItem>
              <strong>Données financières</strong> : montant de l'attestation,
              informations de paiement (traitées via un prestataire sécurisé,
              jamais stockées chez nous).
            </BulletItem>
            <BulletItem>
              <strong>Données de navigation</strong> : adresse IP, pages
              visitées, cookies fonctionnels.
            </BulletItem>
          </BulletList>

          {/* ── 3 ── */}
          <SectionTitle>3. Finalités du traitement</SectionTitle>
          <Paragraph>Vos données sont utilisées pour :</Paragraph>
          <BulletList>
            <BulletItem>Établir et délivrer votre Attestation AVI.</BulletItem>
            <BulletItem>
              Vous accompagner dans vos démarches visa et logement.
            </BulletItem>
            <BulletItem>
              Assurer le suivi de votre dossier et vous informer de son
              avancement.
            </BulletItem>
            <BulletItem>
              Améliorer nos services et personnaliser votre expérience.
            </BulletItem>
            <BulletItem>
              Respecter nos obligations légales et réglementaires.
            </BulletItem>
          </BulletList>

          {/* ── 4 ── */}
          <SectionTitle>4. Base légale</SectionTitle>
          <Paragraph>
            Le traitement de vos données est fondé sur : (a) l'exécution du
            contrat de prestation de services conclu avec vous ; (b) votre
            consentement explicite pour les communications marketing ; (c) nos
            obligations légales.
          </Paragraph>

          {/* ── 5 ── */}
          <SectionTitle>5. Durée de conservation</SectionTitle>
          <Paragraph>
            Vos données sont conservées pendant la durée nécessaire à
            l'exécution de votre contrat, augmentée des délais légaux de
            prescription (5 ans pour les données contractuelles). Les données de
            navigation sont supprimées sous 13 mois.
          </Paragraph>

          {/* ── 6 ── */}
          <SectionTitle>6. Partage des données</SectionTitle>
          <Paragraph>
            Nous ne vendons pas vos données. Elles peuvent être partagées
            uniquement avec :
          </Paragraph>
          <BulletList>
            <BulletItem>
              Nos partenaires bancaires et de paiement, pour le traitement des
              transactions.
            </BulletItem>
            <BulletItem>
              Les établissements universitaires concernés, dans le cadre de la
              validation de votre attestation.
            </BulletItem>
            <BulletItem>
              Les autorités compétentes si la loi l'exige.
            </BulletItem>
          </BulletList>

          {/* ── 7 ── */}
          <SectionTitle>7. Vos droits</SectionTitle>
          <Paragraph>
            Conformément au RGPD et aux lois locales applicables, vous disposez
            des droits suivants :
          </Paragraph>
          <BulletList>
            <BulletItem>Droit d'accès à vos données.</BulletItem>
            <BulletItem>Droit de rectification.</BulletItem>
            <BulletItem>Droit à l'effacement (« droit à l'oubli »).</BulletItem>
            <BulletItem>Droit à la limitation du traitement.</BulletItem>
            <BulletItem>Droit à la portabilité des données.</BulletItem>
            <BulletItem>Droit d'opposition.</BulletItem>
          </BulletList>
          <Paragraph>
            Pour exercer l'un de ces droits, contactez-nous par e-mail. Nous
            répondrons dans un délai de 30 jours.
          </Paragraph>

          {/* ── 8 ── */}
          <SectionTitle>8. Cookies</SectionTitle>
          <Paragraph>
            Notre site utilise des cookies strictement nécessaires au
            fonctionnement (session, sécurité) et des cookies analytiques
            (mesure d'audience anonyme). Vous pouvez configurer votre navigateur
            pour refuser les cookies non essentiels.
          </Paragraph>

          {/* ── 9 ── */}
          <SectionTitle>9. Sécurité</SectionTitle>
          <Paragraph>
            Nous mettons en œuvre des mesures techniques et organisationnelles
            appropriées pour protéger vos données contre tout accès non
            autorisé, perte ou divulgation — chiffrement TLS, accès restreint
            aux données, audits réguliers.
          </Paragraph>

          {/* ── Contact DPO ── */}
          <SectionTitle>10. Contact</SectionTitle>
          <Paragraph>
            Pour toute question relative à cette politique ou pour exercer vos
            droits :
          </Paragraph>
          <ContactBox>
            <ContactBoxTitle>
              Délégué à la protection des données
            </ContactBoxTitle>
            <ContactLine>
              <Mail size={15} color={C_PRIMARY} />
              {config.privacyEmail}
            </ContactLine>
            <ContactLine>
              <Phone size={15} color={C_PRIMARY} />
              {config.phonesFrance}
            </ContactLine>
            <ContactLine>
              <Phone size={15} color={C_PRIMARY} />
              {config.phonesCameroun}
            </ContactLine>
          </ContactBox>
        </DocCard>
      </FadeUpSection>
    </PageWrapper>
  </LandingLayout>
);

export default PrivacyPage;
